import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { RoleBadge } from "@/components/RoleBadge";
import { BrandHeader } from "@/components/BrandHeader";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { AdminFiltersProvider, useAdminFilters } from "@/contexts/AdminFiltersContext";
import {
  LayoutDashboard, Users, Activity, Settings, LogOut,
  FlaskConical, DollarSign, TrendingUp, Inbox, ChevronLeft, Shield, EyeOff, Menu, Gift,
} from "lucide-react";

// Use /housekeeping paths for admin nav so it works in production
const adminNav = [
  { path: "/housekeeping", icon: LayoutDashboard, label: "Overview", end: true },
  { path: "/housekeeping/users", icon: Users, label: "Users" },
  { path: "/housekeeping/growth", icon: TrendingUp, label: "Growth" },
  { path: "/housekeeping/activity", icon: Activity, label: "Activity Logs" },
  { path: "/housekeeping/security", icon: Shield, label: "Security" },
  { path: "/housekeeping/referrals", icon: Gift, label: "Referrals" },
  { path: "/housekeeping/pricing", icon: DollarSign, label: "Promotions" },
  { path: "/housekeeping/experiments", icon: FlaskConical, label: "Experiments" },
];

function HideTestDataToggle() {
  const { hideTestData, setHideTestData } = useAdminFilters();
  return (
    <label
      className="flex items-center gap-2 px-3 py-2 rounded-md text-xs text-[#555555] hover:bg-[#F7F7F7] cursor-pointer select-none transition-colors"
      data-testid="hide-test-data-toggle"
      title="Filters out EM-origin test data across admin views"
    >
      <EyeOff size={14} strokeWidth={1.5} className={hideTestData ? "text-zinc-900" : "text-[#888888]"} />
      <span className="flex-1">Hide test data</span>
      <span className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${hideTestData ? "bg-zinc-900" : "bg-[#D5D5D5]"}`}>
        <input
          type="checkbox"
          className="sr-only"
          checked={hideTestData}
          onChange={(e) => setHideTestData(e.target.checked)}
          data-testid="hide-test-data-checkbox"
        />
        <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${hideTestData ? "translate-x-3.5" : "translate-x-0.5"}`} />
      </span>
    </label>
  );
}

function SidebarNavItems({ onNavigate }) {
  return (
    <>
      {adminNav.map(({ path, icon: Icon, label, end }) => (
        <NavLink
          key={path}
          to={path}
          end={end}
          onClick={onNavigate}
          data-testid={`admin-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
              isActive
                ? "bg-[#F5F5F5] text-[#111111] font-medium"
                : "text-[#555555] hover:bg-[#F7F7F7] hover:text-[#111111]"
            }`
          }
        >
          <Icon size={16} strokeWidth={1.5} />
          {label}
        </NavLink>
      ))}
      <div className="pt-3 mt-3 border-t border-[#E5E5E5]">
        <HideTestDataToggle />
      </div>
    </>
  );
}

function AdminUserIdentity({ user }) {
  const email = user?.email || "";
  const atIdx = email.lastIndexOf("@");
  const localPart = atIdx > 0 ? email.slice(0, atIdx) : email;
  const domain = atIdx > 0 ? email.slice(atIdx + 1) : "";
  // Line 1 label: role (falls back to name, then local part, then "account")
  const roleLabel = user?.role || user?.name || localPart || "account";
  return (
    <div className="flex-1 min-w-0" data-testid="admin-sidebar-user-identity" title={email || undefined}>
      {/* Line 1: role/name + "@domain" badge */}
      <div className="flex items-center gap-1.5 min-w-0">
        <span
          className="text-xs font-medium text-foreground truncate min-w-0 capitalize"
          data-testid="admin-sidebar-user-identity-role"
        >
          {roleLabel}
        </span>
        {domain && (
          <span
            className="shrink-0 inline-flex items-center rounded-md bg-primary text-primary-foreground px-1.5 py-0.5 text-[9px] font-mono font-medium leading-none max-w-[60%] truncate"
            data-testid="admin-sidebar-user-identity-domain"
          >
            @{domain}
          </span>
        )}
      </div>
      {/* Line 2: local part only */}
      {localPart && (
        <p
          className="text-[10px] text-muted-foreground truncate leading-tight mt-0.5"
          data-testid="admin-sidebar-user-identity-username"
        >
          {localPart}
        </p>
      )}
    </div>
  );
}

function SidebarFooter({ user, logout, navigate, onNavigate }) {
  return (
    <div className="p-3 border-t border-[#E5E5E5]">
      {/* Desktop-only "My Inbox" shortcut. On mobile, context switching is
          handled by the always-visible MobileAdminSwitch at the viewport
          bottom, so this shortcut is hidden to avoid duplication. */}
      <button
        onClick={() => { onNavigate && onNavigate(); navigate("/inbox"); }}
        className="hidden md:flex items-center gap-2 px-3 py-2.5 rounded-md text-sm text-[#555555] hover:text-[#111111] hover:bg-[#F7F7F7] transition-colors w-full font-medium"
        data-testid="admin-go-to-inbox"
      >
        <Inbox size={16} />
        My Inbox
      </button>
      <div className="flex items-center gap-2.5 px-3 py-2 mt-1">
        <div className="w-6 h-6 rounded-full bg-[#F5F5F5] flex items-center justify-center text-[10px] font-medium text-[#555555] shrink-0">
          {user?.name?.charAt(0)?.toUpperCase() || "?"}
        </div>
        <AdminUserIdentity user={user} />
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="w-full justify-start text-[#888888] hover:text-[#111111] hover:bg-[#F7F7F7] mt-1 h-8"
        onClick={logout}
        data-testid="admin-logout"
      >
        <LogOut size={13} className="mr-2" /> Sign out
      </Button>
    </div>
  );
}

/**
 * Presence is now driven by usePresenceHeartbeat (mounted globally in App.js).
 * The old admin-only useAdminHeartbeat has been removed — it used last_seen_at
 * which is a fuzzy "last activity" signal, not a true online/offline one.
 */

function AdminLayoutInner() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Auto-close drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const pageTitle = (() => {
    const match = adminNav.find(n => n.end ? location.pathname === n.path : location.pathname.startsWith(n.path));
    return match?.label || "Admin";
  })();

  return (
    <div className="light-theme h-screen flex overflow-hidden bg-white" data-testid="admin-layout">
      {/* Desktop sidebar — ≥1280 px only. Tablets (iPad portrait 768, iPad
          landscape 1024 — both fall below the `xl` breakpoint of 1280px in
          Tailwind's default config) use the drawer, matching phones. */}
      <aside className="hidden xl:flex flex-col w-60 bg-white border-r border-[#E5E5E5] shrink-0" data-testid="admin-sidebar">
        <BrandHeader role={user?.role} subtitle="admin dashboard" />
        <nav className="flex-1 px-3 pt-2 space-y-0.5 overflow-y-auto">
          <SidebarNavItems />
        </nav>
        <SidebarFooter user={user} logout={logout} navigate={navigate} />
      </aside>

      {/* Mobile/tablet drawer — single Sheet root owning both trigger and content. */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        {/* Main content */}
        <main className="flex-1 overflow-hidden flex flex-col min-w-0 xl:pb-0 pb-14">
          {/* Mobile/tablet top bar */}
          <div className="xl:hidden flex items-center justify-between h-12 px-3 border-b border-[#E5E5E5] bg-white shrink-0" data-testid="admin-mobile-topbar">
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-9 w-9 p-0 text-[#555555]"
                data-testid="admin-mobile-menu-toggle"
                aria-label="Open menu"
              >
                <Menu size={18} />
              </Button>
            </SheetTrigger>
            <div className="text-sm font-medium text-[#111111]" data-testid="admin-mobile-title">{pageTitle}</div>
            <RoleBadge role={user?.role} />
          </div>
          <Outlet />
        </main>

        <SheetContent
          side="left"
          className="w-72 p-0 xl:hidden flex flex-col bg-white"
          data-testid="admin-sidebar-drawer"
          style={{
            paddingTop: "env(safe-area-inset-top)",
            paddingBottom: "env(safe-area-inset-bottom)",
          }}
        >
          <BrandHeader role={user?.role} subtitle="admin dashboard" />
          <nav className="flex-1 px-3 pt-2 space-y-0.5 overflow-y-auto">
            <SidebarNavItems onNavigate={() => setMobileOpen(false)} />
          </nav>
          <SidebarFooter user={user} logout={logout} navigate={navigate} onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>
    </div>
  );
}

export default function AdminLayout() {
  return (
    <AdminFiltersProvider>
      <AdminLayoutInner />
    </AdminFiltersProvider>
  );
}
